<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useOvvaBackend } from 'dashboard/composables/useOvvaBackend';

import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';

// Карточка одного канала-мессенджера (telegram/whatsapp/max) внутри вкладки
// «Каналы». Логика — точная копия messengerCard()/saveClientChannel()/
// checkChannel()/revealChannelKey() из platform-backend/views/embed.ejs
// (строки ~2574-2682), просто в виде Vue-компонента вместо строк HTML.
const props = defineProps({
  channel: {
    type: Object,
    required: true,
  },
  clientId: {
    type: Number,
    required: true,
  },
  scopeSourceId: {
    type: String,
    default: '',
  },
});

const emit = defineEmits(['saved']);

const { t } = useI18n();
const { callBackend } = useOvvaBackend();

const CHANNEL_TITLE_KEYS = {
  telegram: 'SOURCE_MGMT.CHANNELS.TITLES.TELEGRAM',
  whatsapp: 'SOURCE_MGMT.CHANNELS.TITLES.WHATSAPP',
  max: 'SOURCE_MGMT.CHANNELS.TITLES.MAX',
};

const form = reactive({
  credential: '',
  botUsername: '',
  externalChannelId: '',
  cwInboxId: '',
  isActive: true,
});

const isSaving = ref(false);
const isChecking = ref(false);
const isRevealing = ref(false);
const checkResult = ref('');
const revealedKey = ref('');

const errors = reactive({
  credential: '',
  botUsername: '',
  externalChannelId: '',
  cwInboxId: '',
});

// Правила — копия normalizeBotId()/assertValidChannelInput() на бэкенде
// (client-channels.service.ts, platform.controller.ts): здесь — чтобы
// ошибка появилась под полем сразу, без запроса.
const BOT_ID_PATTERNS = {
  telegram: v => /^[A-Za-z0-9_]{5,32}$/.test(v) && /bot$/i.test(v),
  max: v => /^[A-Za-z0-9_.-]{3,64}$/.test(v),
  whatsapp: v => /^\d{10,15}$/.test(v.replace(/[\s()+-]/g, '')),
};
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const channelName = computed(() => props.channel.channel);

const validate = () => {
  const key = form.credential.trim();
  const botId = form.botUsername.trim().replace(/^@/, '');
  const channelId = form.externalChannelId.trim();
  const inbox = String(form.cwInboxId ?? '').trim();

  errors.credential = '';
  if (key && /\s/.test(key))
    errors.credential = t('SOURCE_MGMT.CHANNELS.FORM.ERRORS.KEY_SPACES');
  else if (
    key &&
    channelName.value === 'telegram' &&
    !/^\d{5,}:[A-Za-z0-9_-]{30,}$/.test(key)
  )
    errors.credential = t('SOURCE_MGMT.CHANNELS.FORM.ERRORS.KEY_TELEGRAM');
  else if (key && channelName.value !== 'telegram' && key.length < 16)
    errors.credential = t('SOURCE_MGMT.CHANNELS.FORM.ERRORS.KEY_SHORT');

  const hasKey = !!key || props.channel.configured;
  errors.botUsername = '';
  if (!botId && hasKey && form.isActive)
    errors.botUsername = t('SOURCE_MGMT.CHANNELS.FORM.ERRORS.REQUIRED');
  else if (botId && !BOT_ID_PATTERNS[channelName.value]?.(botId))
    errors.botUsername = t(
      `SOURCE_MGMT.CHANNELS.FORM.ERRORS.BOT_ID.${channelName.value}`
    );

  errors.externalChannelId =
    channelName.value === 'whatsapp' && channelId && !UUID.test(channelId)
      ? t('SOURCE_MGMT.CHANNELS.FORM.ERRORS.CHANNEL_ID')
      : '';

  const n = Number(inbox);
  errors.cwInboxId =
    inbox && (!Number.isInteger(n) || n < 1 || n > 2147483647)
      ? t('SOURCE_MGMT.CHANNELS.FORM.ERRORS.INBOX_ID')
      : '';

  return !Object.values(errors).some(Boolean);
};

// Исправил поле — его ошибка уходит, остальные остаются до следующего «Сохранить».
Object.keys(errors).forEach(field => {
  watch(
    () => form[field],
    () => {
      errors[field] = '';
    }
  );
});

// Карточка перерисовывается «с нуля» после каждой перезагрузки списка (как
// и старая панель делала innerHTML=... заново) — ключ никогда не
// предзаполняем (он замаскирован), остальные поля берём из свежих данных.
const resetFromChannel = () => {
  form.credential = '';
  form.botUsername = props.channel.botUsername || '';
  form.externalChannelId = props.channel.externalChannelId || '';
  form.cwInboxId =
    props.channel.cwInboxId == null ? '' : String(props.channel.cwInboxId);
  form.isActive = props.channel.isActive ?? true;
  checkResult.value = '';
  revealedKey.value = '';
  Object.keys(errors).forEach(field => {
    errors[field] = '';
  });
};

watch(() => props.channel, resetFromChannel, { immediate: true });

const saveChannel = async () => {
  if (!validate()) {
    useAlert(t('SOURCE_MGMT.CHANNELS.FORM.ERRORS.FIX_FORM'));
    return;
  }
  isSaving.value = true;
  try {
    const body = {
      sourceId: props.scopeSourceId ? Number(props.scopeSourceId) : null,
      isActive: form.isActive,
      botUsername: form.botUsername.trim(),
      cwInboxId:
        form.cwInboxId !== '' && form.cwInboxId != null
          ? Number(form.cwInboxId)
          : null,
    };
    const credential = form.credential.trim();
    if (credential) body.credential = credential;
    if (props.channel.channel === 'whatsapp') {
      body.externalChannelId = form.externalChannelId.trim();
    }

    const r = await callBackend(
      `/clients/${props.clientId}/channels/${props.channel.channel}`,
      { method: 'PATCH', body }
    );

    const reg = r?.registration;
    if (reg && !reg.ok) {
      useAlert(
        t('SOURCE_MGMT.CHANNELS.API.SAVED_WEBHOOK_FAIL', {
          message: reg.message,
        })
      );
    } else if (reg) {
      useAlert(t('SOURCE_MGMT.CHANNELS.API.SAVED_WEBHOOK_OK'));
    } else {
      useAlert(t('SOURCE_MGMT.CHANNELS.API.SAVED'));
    }
    emit('saved');
  } catch (error) {
    useAlert(
      t('SOURCE_MGMT.CHANNELS.API.SAVE_ERROR', {
        message: error.message || '',
      })
    );
  } finally {
    isSaving.value = false;
  }
};

const checkChannel = async () => {
  isChecking.value = true;
  checkResult.value = '…';
  try {
    const r = await callBackend(
      `/clients/${props.clientId}/channels/${props.channel.channel}/check`,
      {
        method: 'POST',
        body: {
          sourceId: props.scopeSourceId ? Number(props.scopeSourceId) : null,
        },
      }
    );
    if (!r || (!r.message && !r.supported)) {
      checkResult.value = t('SOURCE_MGMT.CHANNELS.API.CHECK_ERROR', {
        message: r?.message || t('SOURCE_MGMT.CHANNELS.API.NO_RESPONSE'),
      });
    } else {
      let prefix = '';
      if (r.ok === true) prefix = '✓ ';
      else if (r.ok === false) prefix = '✕ ';
      checkResult.value = `${prefix}${r.message}`;
    }
  } catch (error) {
    checkResult.value = t('SOURCE_MGMT.CHANNELS.API.CHECK_ERROR', {
      message: error.message || t('SOURCE_MGMT.CHANNELS.API.NO_RESPONSE'),
    });
  } finally {
    isChecking.value = false;
  }
};

const revealKey = async () => {
  isRevealing.value = true;
  try {
    const r = await callBackend(
      `/clients/${props.clientId}/channels/${props.channel.channel}/reveal`,
      {
        method: 'POST',
        body: {
          sourceId: props.scopeSourceId ? Number(props.scopeSourceId) : null,
        },
      }
    );
    if (!r || !r.credential) {
      useAlert(
        t('SOURCE_MGMT.CHANNELS.API.REVEAL_ERROR', {
          message: r?.message || t('SOURCE_MGMT.CHANNELS.API.REVEAL_NEEDS_PAT'),
        })
      );
      return;
    }
    revealedKey.value = r.credential;
  } catch (error) {
    useAlert(
      t('SOURCE_MGMT.CHANNELS.API.REVEAL_ERROR', {
        message:
          error.message || t('SOURCE_MGMT.CHANNELS.API.REVEAL_NEEDS_PAT'),
      })
    );
  } finally {
    isRevealing.value = false;
  }
};
</script>

<template>
  <div
    class="flex flex-col gap-3 p-4 rounded-xl bg-n-alpha-1 outline outline-1 outline-n-container"
  >
    <div class="flex items-center gap-2 flex-wrap">
      <h4 class="text-heading-3 text-n-slate-12 m-0">
        {{ t(CHANNEL_TITLE_KEYS[channel.channel] || channel.channel) }}
      </h4>
      <span
        class="inline-flex items-center rounded-full px-2 py-0.5 text-label-small"
        :class="
          channel.configured
            ? 'bg-n-teal-3 text-n-teal-11'
            : 'bg-n-slate-3 text-n-slate-11'
        "
      >
        {{
          channel.configured
            ? t('SOURCE_MGMT.CHANNELS.FORM.CONFIGURED')
            : t('SOURCE_MGMT.CHANNELS.FORM.NOT_CONFIGURED')
        }}
      </span>
      <span v-if="channel.botUsername" class="text-body-main text-n-slate-11">
        {{
          channel.channel === 'whatsapp'
            ? `· ${channel.botUsername}`
            : `· @${channel.botUsername.replace(/^@/, '')}`
        }}
      </span>
    </div>

    <Input
      v-model="form.credential"
      autocomplete="new-password"
      type="password"
      :label="`${t(`SOURCE_MGMT.CHANNELS.KEY_LABEL.${channel.channel}`)} ${t('SOURCE_MGMT.CHANNELS.FORM.KEY_LABEL_SUFFIX')}`"
      :placeholder="
        channel.configured
          ? t('SOURCE_MGMT.CHANNELS.FORM.KEY_PLACEHOLDER_SET')
          : t('SOURCE_MGMT.CHANNELS.FORM.KEY_PLACEHOLDER_UNSET')
      "
      :message="errors.credential"
      :message-type="errors.credential ? 'error' : 'info'"
    />
    <Input
      v-model="form.botUsername"
      autocomplete="off"
      :label="t(`SOURCE_MGMT.CHANNELS.FORM.BOT_ID.LABEL.${channel.channel}`)"
      :placeholder="
        t(`SOURCE_MGMT.CHANNELS.FORM.BOT_ID.PLACEHOLDER.${channel.channel}`)
      "
      :message="
        errors.botUsername ||
        t(`SOURCE_MGMT.CHANNELS.FORM.BOT_ID.HELP.${channel.channel}`)
      "
      :message-type="errors.botUsername ? 'error' : 'info'"
    />
    <Input
      v-if="channel.channel === 'whatsapp'"
      v-model="form.externalChannelId"
      autocomplete="off"
      :label="t('SOURCE_MGMT.CHANNELS.FORM.CHANNEL_ID_LABEL')"
      :placeholder="t('SOURCE_MGMT.CHANNELS.FORM.CHANNEL_ID_PLACEHOLDER')"
      :message="errors.externalChannelId"
      :message-type="errors.externalChannelId ? 'error' : 'info'"
    />
    <Input
      v-model="form.cwInboxId"
      autocomplete="off"
      type="number"
      :label="t('SOURCE_MGMT.CHANNELS.FORM.INBOX_LABEL')"
      :placeholder="t('SOURCE_MGMT.CHANNELS.FORM.INBOX_PLACEHOLDER')"
      :message="errors.cwInboxId"
      :message-type="errors.cwInboxId ? 'error' : 'info'"
    />
    <label class="flex items-center gap-2 cursor-pointer w-fit">
      <Switch v-model="form.isActive" />
      <span class="text-body-main text-n-slate-12">
        {{ t('SOURCE_MGMT.CHANNELS.FORM.ACTIVE_LABEL') }}
      </span>
    </label>

    <div class="flex flex-col gap-1">
      <span class="text-label-small text-n-slate-11">
        {{
          channel.webhookUrl
            ? t('SOURCE_MGMT.CHANNELS.FORM.WEBHOOK_LABEL')
            : t('SOURCE_MGMT.CHANNELS.FORM.WEBHOOK_PENDING')
        }}
      </span>
      <span
        v-if="channel.webhookUrl"
        class="text-label-small text-n-slate-11 break-all select-all"
      >
        {{ channel.webhookUrl }}
      </span>
    </div>

    <div class="flex items-center gap-2 flex-wrap">
      <NextButton
        size="sm"
        :label="t('SOURCE_MGMT.CHANNELS.FORM.SAVE')"
        :is-loading="isSaving"
        @click="saveChannel"
      />
      <NextButton
        v-if="channel.configured"
        variant="faded"
        color="slate"
        size="sm"
        :label="t('SOURCE_MGMT.CHANNELS.FORM.REVEAL')"
        :is-loading="isRevealing"
        @click="revealKey"
      />
      <NextButton
        v-if="channel.configured"
        variant="faded"
        color="slate"
        size="sm"
        :label="t('SOURCE_MGMT.CHANNELS.FORM.CHECK')"
        :is-loading="isChecking"
        @click="checkChannel"
      />
    </div>

    <div v-if="checkResult" class="text-body-main text-n-slate-11">
      {{ checkResult }}
    </div>
    <div
      v-if="revealedKey"
      class="text-body-main text-n-slate-12 break-all select-all"
    >
      {{ revealedKey }}
    </div>
  </div>
</template>
